import React, { useState, useRef, useEffect } from 'react';
import ProgressBar from './ProgressBar';
import { getApiUrl, adminAPI, fileAPI, pitchEventAPI } from '../lib/api';
import SearchableDropdown from './SearchableDropdown';

const categories = [
	// Pitch & Presentation
	{ value: 'PITCH_DECK', label: 'Pitch Deck', group: 'Pitch & Presentation' },
	{ value: 'INVESTOR_PRESENTATION', label: 'Investor Presentation', group: 'Pitch & Presentation' },
	{ value: 'EXECUTIVE_SUMMARY', label: 'Executive Summary', group: 'Pitch & Presentation' },

	// Financial Documents
	{ value: 'FINANCIAL', label: 'Financial Statements', group: 'Financial Documents' },
	{ value: 'AUDITED_FINANCIALS', label: 'Audited Financials', group: 'Financial Documents' },
	{ value: 'PROJECTED_FINANCIALS', label: 'Projected Financials', group: 'Financial Documents' },
	{ value: 'BALANCE_SHEET', label: 'Balance Sheet', group: 'Financial Documents' },
	{ value: 'P_L_STATEMENT', label: 'P&L Statement', group: 'Financial Documents' },
	{ value: 'CASH_FLOW_STATEMENT', label: 'Cash Flow Statement', group: 'Financial Documents' },
	{ value: 'VALUATION_CERTIFICATE', label: 'Valuation Certificate', group: 'Financial Documents' },
	{ value: 'CAP_TABLE', label: 'Cap Table', group: 'Financial Documents' },

	// Legal Documents
	{ value: 'LEGAL', label: 'Legal Documents', group: 'Legal Documents' },
	{ value: 'INCORPORATION', label: 'Incorporation Documents', group: 'Legal Documents' },
	{ value: 'AGREEMENT', label: 'Agreements', group: 'Legal Documents' },
	{ value: 'SHA', label: 'Shareholder Agreement (SHA)', group: 'Legal Documents' },
	{ value: 'TERM_SHEET', label: 'Term Sheet', group: 'Legal Documents' },
	{ value: 'CCD', label: 'Compulsory Convertible Debentures (CCD)', group: 'Legal Documents' },
	{ value: 'SAFE_AGREEMENT', label: 'SAFE Agreement', group: 'Legal Documents' },
	{ value: 'CONVERTIBLE_NOTE', label: 'Convertible Note', group: 'Legal Documents' },
	{ value: 'EMPLOYMENT_AGREEMENT', label: 'Employment Agreement', group: 'Legal Documents' },
	{ value: 'NDA', label: 'Non-Disclosure Agreement (NDA)', group: 'Legal Documents' },
	{ value: 'IP_DOCUMENTS', label: 'IP Documents', group: 'Legal Documents' },
	{ value: 'PATENTS', label: 'Patents', group: 'Legal Documents' },
	{ value: 'TRADEMARKS', label: 'Trademarks', group: 'Legal Documents' },

	// Business Documents
	{ value: 'BUSINESS_PLAN', label: 'Business Plan', group: 'Business Documents' },
	{ value: 'MARKET_RESEARCH', label: 'Market Research', group: 'Business Documents' },
	{ value: 'COMPETITIVE_ANALYSIS', label: 'Competitive Analysis', group: 'Business Documents' },
	{ value: 'BUSINESS_MODEL', label: 'Business Model', group: 'Business Documents' },
	{ value: 'GO_TO_MARKET', label: 'Go-to-Market Strategy', group: 'Business Documents' },
	{ value: 'PRODUCT_DOCUMENTATION', label: 'Product Documentation', group: 'Business Documents' },
	{ value: 'TECHNICAL_SPECS', label: 'Technical Specifications', group: 'Business Documents' },

	// Compliance & Regulatory
	{ value: 'COMPLIANCE', label: 'Compliance Documents', group: 'Compliance & Regulatory' },
	{ value: 'LICENSES', label: 'Licenses & Permits', group: 'Compliance & Regulatory' },
	{ value: 'REGULATORY_FILINGS', label: 'Regulatory Filings', group: 'Compliance & Regulatory' },
	{ value: 'TAX_DOCUMENTS', label: 'Tax Documents', group: 'Compliance & Regulatory' },

	// Operations
	{ value: 'OPERATIONAL', label: 'Operational Documents', group: 'Operations' },
	{ value: 'HR_DOCUMENTS', label: 'HR Documents', group: 'Operations' },
	{ value: 'POLICIES', label: 'Company Policies', group: 'Operations' },
	{ value: 'PROCESSES', label: 'Process Documents', group: 'Operations' },

	// Marketing & Sales
	{ value: 'MARKETING_MATERIALS', label: 'Marketing Materials', group: 'Marketing & Sales' },
	{ value: 'SALES_DECK', label: 'Sales Deck', group: 'Marketing & Sales' },
	{ value: 'BRAND_GUIDELINES', label: 'Brand Guidelines', group: 'Marketing & Sales' },

	// Other
	{ value: 'GENERAL', label: 'General Documents', group: 'Other' },
	{ value: 'OTHER', label: 'Other', group: 'Other' }
];

const FileUpload = ({
	onUploadSuccess,
	onUploadError,
	className = '',
	moduleType = 'GENERAL',
	moduleId = null,
	defaultCategory = 'GENERAL',
	defaultVisibility = 'PRIVATE'
}) => {
	const [isDragging, setIsDragging] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isUploading, setIsUploading] = useState(false);
	const [fileCategory, setFileCategory] = useState(defaultCategory);
	const [visibility, setVisibility] = useState(defaultVisibility);
	const [description, setDescription] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const fileInputRef = useRef(null);
	const categoryDropdownRef = useRef(null);

	// New state for user sharing
	const [selectedUsers, setSelectedUsers] = useState([]); // List of selected user IDs
	const [availableUsers, setAvailableUsers] = useState([]); // List of available users to share with
	const [isLoadingUsers, setIsLoadingUsers] = useState(false);
	const [showUserSelection, setShowUserSelection] = useState(false);
	const [userSearchTerm, setUserSearchTerm] = useState('');

	// New state for network sharing
	const [selectedNetworks, setSelectedNetworks] = useState([]);
	const [showNetworkSelection, setShowNetworkSelection] = useState(false);
	const [networkSearchTerm, setNetworkSearchTerm] = useState('');

	// New state for event sharing
	const [selectedEvents, setSelectedEvents] = useState([]);
	const [showEventSelection, setShowEventSelection] = useState(false);
	const [eventSearchTerm, setEventSearchTerm] = useState('');
	const [availableEvents, setAvailableEvents] = useState([]);
	const [isLoadingEvents, setIsLoadingEvents] = useState(false);

	// Fetch users/networks/events based on visibility
	useEffect(() => {
		if (visibility === 'PRIVATE' || visibility === 'NETWORK') {
			loadUsers();
		}

		if (visibility === 'EVENT') {
			if (typeof pitchEventAPI === 'undefined') {
				console.error("pitchEventAPI is undefined! Check api.js exports.");
				return;
			}
			loadEvents();
		}

		if (visibility !== 'PRIVATE') {
			setSelectedUsers([]);
		}

		if (visibility !== 'NETWORK') {
			setSelectedNetworks([]);
		}

		if (visibility !== 'EVENT') {
			setSelectedEvents([]);
		}
	}, [visibility]);

	const loadEvents = async () => {
		setIsLoadingEvents(true);
		try {
			const events = await pitchEventAPI.getAllEvents();
			setAvailableEvents(events || []);
		} catch (error) {
			console.error("Failed to load events:", error);
			// Fallback or empty
			setAvailableEvents([]);
		} finally {
			setIsLoadingEvents(false);
		}
	};

	const loadUsers = async () => {
		setIsLoadingUsers(true);
		try {
			// Try fetching related investors/users first (context-aware)
			try {
				const relatedInvestors = await fileAPI.getRelatedInvestors();
				if (relatedInvestors && relatedInvestors.length > 0) {
					setAvailableUsers(relatedInvestors);
					return;
				}
			} catch (e) {
				// Ignore and fallback
				console.log("Could not load related investors, falling back to all users");
			}

			// Fallback to all users (for admins etc)
			const allUsers = await adminAPI.getAllUsers();
			setAvailableUsers(allUsers || []);

		} catch (error) {
			console.error("Failed to load users for sharing:", error);
		} finally {
			setIsLoadingUsers(false);
		}
	};

	// Helper to format users for dropdown
	const userOptions = React.useMemo(() => {
		return availableUsers.map(user => ({
			value: user.id,
			label: `${user.name} (${user.email})`,
			description: user.role
		}));
	}, [availableUsers]);

	const toggleUser = (userId) => {
		setSelectedUsers(prev => {
			if (prev.includes(userId)) {
				return prev.filter(id => id !== userId);
			} else {
				return [...prev, userId];
			}
		});
	};

	// Filter users for selection
	const filteredUsers = React.useMemo(() => {
		return availableUsers.filter(user =>
		(user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
			user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
			(user.role && user.role.toLowerCase().includes(userSearchTerm.toLowerCase())))
		);
	}, [availableUsers, userSearchTerm]);

	// Filter networks for selection
	const availableNetworks = React.useMemo(() => {
		return availableUsers.filter(user => user.role === 'INVESTOR_NETWORK');
	}, [availableUsers]);

	const filteredNetworks = React.useMemo(() => {
		return availableNetworks.filter(network =>
			network.name.toLowerCase().includes(networkSearchTerm.toLowerCase()) ||
			network.email.toLowerCase().includes(networkSearchTerm.toLowerCase()) ||
			(network.organizationName && network.organizationName.toLowerCase().includes(networkSearchTerm.toLowerCase()))
		);
	}, [availableNetworks, networkSearchTerm]);


	const toggleNetwork = (networkId) => {
		setSelectedNetworks(prev => {
			if (prev.includes(networkId)) {
				return prev.filter(id => id !== networkId);
			} else {
				return [...prev, networkId];
			}
		});
	};

	// Filter events for selection
	const filteredEvents = React.useMemo(() => {
		if (!Array.isArray(availableEvents)) return [];
		return availableEvents.filter(event =>
			event &&
			(event.title || '').toLowerCase().includes(eventSearchTerm.toLowerCase()) ||
			(event.description && event.description.toLowerCase().includes(eventSearchTerm.toLowerCase()))
		);
	}, [availableEvents, eventSearchTerm]);

	// ... (skipping unchanged toggleEvent)

	// inside rendering loop:
	{
		filteredEvents.map(event => (
			<div
				key={event.id || Math.random()}
				className={`flex items-center px-6 py-4 cursor-pointer transition-colors ${selectedEvents.includes(event.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
				onClick={() => toggleEvent(event.id)}
			>
				<div className="flex-shrink-0">
					<input
						type="checkbox"
						checked={selectedEvents.includes(event.id)}
						readOnly
						className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
					/>
				</div>
				<div className="ml-4 flex-1">
					<div className="flex items-center justify-between">
						<p className={`text-sm font-medium ${selectedEvents.includes(event.id) ? 'text-blue-900' : 'text-gray-900'}`}>
							{event.title || 'Untitled Event'}
						</p>
						<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
							{event.date ? new Date(event.date).toLocaleDateString() : 'Date N/A'}
						</span>
					</div>
					<p className="text-sm text-gray-500 truncate">{event.description || 'No description'}</p>
				</div>
			</div>
		))
	}

	const toggleEvent = (eventId) => {
		setSelectedEvents(prev => {
			if (prev.includes(eventId)) {
				return prev.filter(id => id !== eventId);
			} else {
				return [...prev, eventId];
			}
		});
	};



	// Filter categories based on search term
	const filteredCategories = React.useMemo(() => {
		return searchTerm
			? categories.filter(cat =>
				cat.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
				cat.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
				cat.group.toLowerCase().includes(searchTerm.toLowerCase())
			)
			: categories;
	}, [searchTerm]);

	// Group filtered categories
	const filteredGroupedCategories = React.useMemo(() => {
		return filteredCategories.reduce((acc, cat) => {
			const group = cat.group || 'Other';
			if (!acc[group]) {
				acc[group] = [];
			}
			acc[group].push(cat);
			return acc;
		}, {});
	}, [filteredCategories]);

	// Get selected category label
	const selectedCategoryLabel = categories.find(cat => cat.value === fileCategory)?.label || 'Select Document Type';

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
				setShowCategoryDropdown(false);
			}
		};

		if (showCategoryDropdown) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showCategoryDropdown]);

	const visibilityOptions = [
		{ value: 'PRIVATE', label: 'Private', description: 'Only you and explicitly granted users', icon: '🔒' },
		{ value: 'PUBLIC', label: 'Public', description: 'All users in your organization', icon: '🌐' },
		{ value: 'NETWORK', label: 'Network', description: 'All network users', icon: '👥' },
		{ value: 'EVENT', label: 'Event', description: 'Event participants', icon: '📅' }
	];

	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const handleDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);

		const files = Array.from(e.dataTransfer.files);
		if (files.length > 0) {
			setSelectedFile(files[0]);
		}
	};

	const handleFileSelect = (e) => {
		const files = e.target.files;
		if (files.length > 0) {
			setSelectedFile(files[0]);
		}
	};

	const handleFileUpload = async () => {
		if (!selectedFile) return;

		// Validate file size (100MB limit)
		const maxSize = 100 * 1024 * 1024;
		if (selectedFile.size > maxSize) {
			onUploadError && onUploadError('File size exceeds 100MB limit');
			return;
		}

		// Validate file type
		const allowedTypes = [
			'application/pdf',
			'application/msword',
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
			'application/vnd.ms-excel',
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			'application/vnd.ms-powerpoint',
			'application/vnd.openxmlformats-officedocument.presentationml.presentation',
			'text/plain',
			'text/csv',
			'image/jpeg',
			'image/png',
			'image/gif',
			'image/webp'
		];

		if (!allowedTypes.includes(selectedFile.type)) {
			onUploadError && onUploadError('File type not supported');
			return;
		}

		setIsUploading(true);
		setUploadProgress(0);

		try {
			const formData = new FormData();
			formData.append('file', selectedFile);
			formData.append('moduleType', moduleType);
			if (moduleId) formData.append('moduleId', moduleId);
			formData.append('fileCategory', fileCategory);
			formData.append('fileCategory', fileCategory);
			formData.append('visibility', visibility);
			if (description) formData.append('description', description);

			// Add shared users if Private
			if (visibility === 'PRIVATE' && selectedUsers.length > 0) {
				selectedUsers.forEach(userId => {
					formData.append('sharedWith', userId);
				});
			}

			// Add shared networks if Network visibility
			if (visibility === 'NETWORK' && selectedNetworks.length > 0) {
				selectedNetworks.forEach(networkId => {
					formData.append('sharedWithNetworks', networkId);
				});
			}

			// Add shared events if Event visibility
			if (visibility === 'EVENT' && selectedEvents.length > 0) {
				selectedEvents.forEach(eventId => {
					formData.append('sharedWithEvents', eventId);
				});
			}

			const xhr = new XMLHttpRequest();

			xhr.upload.addEventListener('progress', (e) => {
				if (e.lengthComputable) {
					const progress = Math.round((e.loaded / e.total) * 100);
					setUploadProgress(progress);
				}
			});

			xhr.addEventListener('load', () => {
				if (xhr.status === 200) {
					setUploadProgress(100);
					// specific delay to show 100% completion
					setTimeout(() => {
						onUploadSuccess && onUploadSuccess(selectedFile.name);
						// Reset form
						setDescription('');
						setSelectedFile(null);
						setUploadProgress(0);
						setIsUploading(false);
						// Reset file input
						if (fileInputRef.current) {
							fileInputRef.current.value = '';
						}
					}, 1000);
				} else {
					onUploadError && onUploadError('Upload failed');
					setIsUploading(false);
				}
			});

			xhr.addEventListener('error', () => {
				onUploadError && onUploadError('Upload failed');
				setIsUploading(false);
			});

			xhr.open('POST', getApiUrl('/files/upload'));
			xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
			xhr.send(formData);

		} catch (error) {
			onUploadError && onUploadError(error.message || 'Failed to upload file');
			setIsUploading(false);
		}
	};

	// Modal close handler
	const handleCancel = () => {
		setSelectedFile(null);
		setDescription('');
		setFileCategory(defaultCategory); // Reset to default
		setVisibility(defaultVisibility); // Reset to default
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	return (
		<div className={`file-upload ${className}`}>
			{/* Step 1: Clean Dropzone / Trigger */}
			<div
				className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 cursor-pointer
                    ${isDragging
						? 'border-blue-500 bg-blue-50'
						: 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
					}`}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				onClick={() => !isUploading && fileInputRef.current?.click()}
			>
				<input
					ref={fileInputRef}
					type="file"
					className="hidden"
					onChange={handleFileSelect}
					disabled={isUploading}
					accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp"
				/>

				<div className="flex flex-col items-center justify-center space-y-3">
					<div className={`w-12 h-12 rounded-full flex items-center justify-center
						${isDragging ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}
					`}>
						<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
						</svg>
					</div>
					<div>
						<p className="text-sm font-medium text-gray-900">
							<span className="text-blue-600 hover:underline">Click to upload</span> or drag and drop
						</p>
						<p className="text-xs text-gray-500 mt-1">
							PDF, Office docs, Images (max. 100MB)
						</p>
					</div>
				</div>
			</div>

			{/* Step 2: Details Modal */}
			{selectedFile && (
				<div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
					<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
						{/* Background overlay */}
						<div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleCancel}></div>

						{/* Modal panel */}
						<div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
							{showUserSelection ? (
								<div className="flex flex-col h-[600px]">
									{/* User Selection Header */}
									<div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
										<div>
											<h3 className="text-lg font-medium text-gray-900">Select Members</h3>
											<p className="text-sm text-gray-500">Select users to share this file with</p>
										</div>
										<button
											onClick={() => setShowUserSelection(false)}
											className="text-gray-400 hover:text-gray-500 transition-colors"
										>
											<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
											</svg>
										</button>
									</div>

									{/* Search */}
									<div className="px-6 py-4 border-b border-gray-200 bg-white">
										<div className="relative">
											<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
												<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
												</svg>
											</div>
											<input
												type="text"
												placeholder="Search by name, email or role..."
												className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
												value={userSearchTerm}
												onChange={e => setUserSearchTerm(e.target.value)}
												autoFocus
											/>
										</div>
									</div>

									{/* List */}
									<div className="flex-1 overflow-y-auto bg-white">
										{isLoadingUsers ? (
											<div className="flex justify-center items-center h-full">
												<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
											</div>
										) : filteredUsers.length > 0 ? (
											<div className="divide-y divide-gray-100">
												{filteredUsers.map(user => (
													<div
														key={user.id}
														className={`flex items-center px-6 py-4 cursor-pointer transition-colors ${selectedUsers.includes(user.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
														onClick={() => toggleUser(user.id)}
													>
														<div className="flex-shrink-0">
															<input
																type="checkbox"
																checked={selectedUsers.includes(user.id)}
																readOnly
																className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
															/>
														</div>
														<div className="ml-4 flex-1">
															<div className="flex items-center justify-between">
																<p className={`text-sm font-medium ${selectedUsers.includes(user.id) ? 'text-blue-900' : 'text-gray-900'}`}>
																	{user.name}
																</p>
																<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
																	{user.role}
																</span>
															</div>
															<p className="text-sm text-gray-500">{user.email}</p>
														</div>
													</div>
												))}
											</div>
										) : (
											<div className="flex flex-col items-center justify-center h-full text-gray-500">
												<svg className="w-12 h-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
												</svg>
												<p>No users found</p>
											</div>
										)}
									</div>

									{/* Footer */}
									<div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
										<div className="text-sm text-gray-500">
											{selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
										</div>
										<button
											onClick={() => setShowUserSelection(false)}
											className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
										>
											Done
										</button>
									</div>
								</div>
							) : showNetworkSelection ? (
								<div className="flex flex-col h-[600px]">
									{/* Network Selection Header */}
									<div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
										<div>
											<h3 className="text-lg font-medium text-gray-900">Select Networks</h3>
											<p className="text-sm text-gray-500">Select investor networks to share this file with</p>
										</div>
										<button
											onClick={() => setShowNetworkSelection(false)}
											className="text-gray-400 hover:text-gray-500 transition-colors"
										>
											<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
											</svg>
										</button>
									</div>

									{/* Search */}
									<div className="px-6 py-4 border-b border-gray-200 bg-white">
										<div className="relative">
											<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
												<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
												</svg>
											</div>
											<input
												type="text"
												placeholder="Search networks..."
												className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
												value={networkSearchTerm}
												onChange={e => setNetworkSearchTerm(e.target.value)}
												autoFocus
											/>
										</div>
									</div>

									{/* List */}
									<div className="flex-1 overflow-y-auto bg-white">
										{isLoadingUsers ? (
											<div className="flex justify-center items-center h-full">
												<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
											</div>
										) : filteredNetworks.length > 0 ? (
											<div className="divide-y divide-gray-100">
												{filteredNetworks.map(network => (
													<div
														key={network.id}
														className={`flex items-center px-6 py-4 cursor-pointer transition-colors ${selectedNetworks.includes(network.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
														onClick={() => toggleNetwork(network.id)}
													>
														<div className="flex-shrink-0">
															<input
																type="checkbox"
																checked={selectedNetworks.includes(network.id)}
																readOnly
																className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
															/>
														</div>
														<div className="ml-4 flex-1">
															<div className="flex items-center justify-between">
																<p className={`text-sm font-medium ${selectedNetworks.includes(network.id) ? 'text-blue-900' : 'text-gray-900'}`}>
																	{network.name}
																</p>
																<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
																	{network.organizationName || 'Network'}
																</span>
															</div>
															<p className="text-sm text-gray-500">{network.email}</p>
														</div>
													</div>
												))}
											</div>
										) : (
											<div className="flex flex-col items-center justify-center h-full text-gray-500">
												<svg className="w-12 h-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
												</svg>
												<p>No networks found</p>
											</div>
										)}
									</div>

									{/* Footer */}
									<div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
										<div className="text-sm text-gray-500">
											{selectedNetworks.length} network{selectedNetworks.length !== 1 ? 's' : ''} selected
										</div>
										<button
											onClick={() => setShowNetworkSelection(false)}
											className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
										>
											Done
										</button>
									</div>
								</div>
							) : showEventSelection ? (
								<div className="flex flex-col h-[600px]">
									{/* Event Selection Header */}
									<div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
										<div>
											<h3 className="text-lg font-medium text-gray-900">Select Events</h3>
											<p className="text-sm text-gray-500">Select events to share this file with participants</p>
										</div>
										<button
											onClick={() => setShowEventSelection(false)}
											className="text-gray-400 hover:text-gray-500 transition-colors"
										>
											<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
											</svg>
										</button>
									</div>

									{/* Search */}
									<div className="px-6 py-4 border-b border-gray-200 bg-white">
										<div className="relative">
											<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
												<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
												</svg>
											</div>
											<input
												type="text"
												placeholder="Search events..."
												className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
												value={eventSearchTerm}
												onChange={e => setEventSearchTerm(e.target.value)}
												autoFocus
											/>
										</div>
									</div>

									{/* List */}
									<div className="flex-1 overflow-y-auto bg-white">
										{isLoadingEvents ? (
											<div className="flex justify-center items-center h-full">
												<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
											</div>
										) : filteredEvents.length > 0 ? (
											<div className="divide-y divide-gray-100">
												{filteredEvents.map(event => (
													<div
														key={event.id || Math.random()}
														className={`flex items-center px-6 py-4 cursor-pointer transition-colors ${selectedEvents.includes(event.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
														onClick={() => toggleEvent(event.id)}
													>
														<div className="flex-shrink-0">
															<input
																type="checkbox"
																checked={selectedEvents.includes(event.id)}
																readOnly
																className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
															/>
														</div>
														<div className="ml-4 flex-1">
															<div className="flex items-center justify-between">
																<p className={`text-sm font-medium ${selectedEvents.includes(event.id) ? 'text-blue-900' : 'text-gray-900'}`}>
																	{event.title || 'Untitled Event'}
																</p>
																<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
																	{event.date ? new Date(event.date).toLocaleDateString() : 'Date N/A'}
																</span>
															</div>
															<p className="text-sm text-gray-500 truncate">{event.description || 'No description'}</p>
														</div>
													</div>
												))}
											</div>
										) : (
											<div className="flex flex-col items-center justify-center h-full text-gray-500">
												<svg className="w-12 h-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
												</svg>
												<p>No events found</p>
											</div>
										)}
									</div>

									{/* Footer */}
									<div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
										<div className="text-sm text-gray-500">
											{selectedEvents.length} event{selectedEvents.length !== 1 ? 's' : ''} selected
										</div>
										<button
											onClick={() => setShowEventSelection(false)}
											className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
										>
											Done
										</button>
									</div>
								</div>
							) : (
								<>
									<div className="bg-white px-6 pt-6 pb-4">
										<div className="w-full">
											<h3 className="text-xl font-semibold text-gray-900 mb-6 border-b pb-4" id="modal-title">
												Upload File
											</h3>

											{/* File Preview Card */}
											<div className="flex items-center p-4 bg-gray-50 border border-gray-200 rounded-lg mb-6">
												<div className="flex-shrink-0 w-12 h-12 bg-white border border-gray-200 rounded-lg flex items-center justify-center text-blue-600 shadow-sm">
													<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
													</svg>
												</div>
												<div className="ml-4 flex-1 min-w-0">
													<p className="text-sm font-semibold text-gray-900 truncate">{selectedFile.name}</p>
													<p className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
												</div>
												<button
													onClick={handleCancel}
													className="ml-4 text-sm text-red-600 hover:text-red-800 font-medium"
												>
													Change
												</button>
											</div>

											<div className="space-y-6">
												{/* Document Type */}
												<div className="relative" ref={categoryDropdownRef}>
													<label className="block text-sm font-medium text-gray-700 mb-2">
														Document Type <span className="text-red-500">*</span>
													</label>
													<div className="relative">
														<button
															type="button"
															onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
															className="w-full bg-white border border-gray-300 rounded-lg shadow-sm pl-4 pr-10 py-2.5 text-left cursor-default focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
														>
															<span className={`block truncate ${fileCategory ? 'text-gray-900' : 'text-gray-400'}`}>
																{selectedCategoryLabel}
															</span>
															<span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
																<svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="none" stroke="currentColor">
																	<path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
																</svg>
															</span>
														</button>

														{showCategoryDropdown && (
															<div className="absolute z-10 mt-1 w-full bg-white shadow-xl max-h-80 rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm flex flex-col overflow-hidden">
																{/* Fixed Search Bar */}
																<div className="p-2 border-b border-gray-100 bg-white z-20 shrink-0">
																	<input
																		type="text"
																		className="w-full border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
																		placeholder="Search types..."
																		value={searchTerm}
																		onChange={(e) => setSearchTerm(e.target.value)}
																		autoFocus
																	/>
																</div>

																{/* Scrollable List */}
																<div className="overflow-y-auto flex-1">
																	{Object.entries(filteredGroupedCategories).map(([group, groupCats]) => (
																		<div key={group}>
																			<div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider sticky top-0 z-10 border-b border-gray-100">
																				{group}
																			</div>
																			{groupCats.map((cat) => (
																				<div
																					key={cat.value}
																					className={`cursor-pointer select-none relative py-2.5 pl-3 pr-9 hover:bg-blue-50 ${fileCategory === cat.value ? 'bg-blue-50 text-blue-900' : 'text-gray-900'}`}
																					onClick={() => {
																						setFileCategory(cat.value);
																						setShowCategoryDropdown(false);
																						setSearchTerm('');
																					}}
																				>
																					<span className={`block truncate ${fileCategory === cat.value ? 'font-semibold' : 'font-normal'}`}>
																						{cat.label}
																					</span>
																					{fileCategory === cat.value && (
																						<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
																							<svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
																								<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
																							</svg>
																						</span>
																					)}
																				</div>
																			))}
																		</div>
																	))}
																</div>
															</div>
														)}
													</div>
												</div>

												{/* Visibility - Grid Cols 4 for wider layout */}
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-2">
														Who can view this file?
													</label>
													<div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
														{visibilityOptions.map((opt) => (
															<div
																key={opt.value}
																onClick={() => setVisibility(opt.value)}
																className={`cursor-pointer border rounded-lg p-3 flex flex-col items-center text-center transition-all h-full ${visibility === opt.value
																	? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
																	: 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
																	}`}
															>
																<span className="text-2xl mb-2">{opt.icon}</span>
																<p className={`text-sm font-medium mb-1 ${visibility === opt.value ? 'text-blue-900' : 'text-gray-900'}`}>
																	{opt.label}
																</p>
																<p className="text-xs text-gray-500 leading-tight mt-1">
																	{opt.description}
																</p>
															</div>
														))}
													</div>
												</div>

												{/* User Selection for Private Visibility */}
												{visibility === 'PRIVATE' && (
													<div className="mt-4 animate-fadeIn">
														<label className="block text-sm font-medium text-gray-700 mb-2">
															Share with specific members (Optional)
														</label>
														<div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
															<div className="flex-1">
																<p className="text-sm text-gray-900 font-medium">
																	{selectedUsers.length > 0
																		? `${selectedUsers.length} user${selectedUsers.length !== 1 ? 's' : ''} selected`
																		: 'No users selected'}
																</p>
																<p className="text-xs text-gray-500 mt-1">
																	Selected users will be granted View and Download permissions.
																</p>
															</div>
															<button
																type="button"
																onClick={() => setShowUserSelection(true)}
																className="ml-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
															>
																{isLoadingUsers ? 'Loading...' : 'Select Members'}
															</button>
														</div>
													</div>
												)}

												{/* Network Selection for Network Visibility */}
												{visibility === 'NETWORK' && (
													<div className="mt-4 animate-fadeIn">
														<label className="block text-sm font-medium text-gray-700 mb-2">
															Share with specific networks (Optional)
														</label>
														<div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
															<div className="flex-1">
																<p className="text-sm text-gray-900 font-medium">
																	{selectedNetworks.length > 0
																		? `${selectedNetworks.length} network${selectedNetworks.length !== 1 ? 's' : ''} selected`
																		: 'All network users'}
																</p>
																<p className="text-xs text-gray-500 mt-1">
																	{selectedNetworks.length > 0
																		? 'Selected networks will be granted View and Download permissions.'
																		: 'Select networks to limit access, otherwise visible to all.'}
																</p>
															</div>
															<button
																type="button"
																onClick={() => setShowNetworkSelection(true)}
																className="ml-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
															>
																{isLoadingUsers ? 'Loading...' : 'Select Networks'}
															</button>
														</div>
													</div>
												)}

												{/* Event Selection for Event Visibility */}
												{visibility === 'EVENT' && (
													<div className="mt-4 animate-fadeIn">
														<label className="block text-sm font-medium text-gray-700 mb-2">
															Share with specific events (Optional)
														</label>
														<div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
															<div className="flex-1">
																<p className="text-sm text-gray-900 font-medium">
																	{selectedEvents.length > 0
																		? `${selectedEvents.length} event${selectedEvents.length !== 1 ? 's' : ''} selected`
																		: 'Event participants'}
																</p>
																<p className="text-xs text-gray-500 mt-1">
																	{selectedEvents.length > 0
																		? 'Participants of selected events will get access.'
																		: 'Select events to limit access.'}
																</p>
															</div>
															<button
																type="button"
																onClick={() => setShowEventSelection(true)}
																className="ml-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
															>
																{isLoadingEvents ? 'Loading...' : 'Select Events'}
															</button>
														</div>
													</div>
												)}

												{/* Description */}
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-2">
														Description
													</label>
													<textarea
														rows={2}
														className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-lg p-3"
														placeholder="Enter a brief description..."
														value={description}
														onChange={(e) => setDescription(e.target.value)}
													/>
												</div>
											</div>
										</div>
									</div>

									{/* Footer */}
									<div className="bg-gray-50 px-6 py-4 flex flex-row-reverse gap-3">
										<button
											type="button"
											onClick={handleFileUpload}
											disabled={isUploading}
											className={`inline-flex justify-center rounded-lg border border-transparent shadow-sm px-6 py-2.5 bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isUploading ? 'opacity-70 cursor-not-allowed' : ''}`}
										>
											{isUploading ? 'Uploading...' : 'Upload File'}
										</button>
										<button
											type="button"
											onClick={handleCancel}
											disabled={isUploading}
											className="inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-6 py-2.5 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
										>
											Cancel
										</button>
									</div>

									{/* Progress Bar */}
									{isUploading && (
										<div className="px-6 pb-6">
											<ProgressBar progress={uploadProgress} label="Uploading" />
										</div>
									)}
								</>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default FileUpload;